import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatError } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import { formatTime } from "@/lib/time";

const LABELS = { yes: "I’m in", no: "Can’t make it", maybe: "Maybe" };

export default function Rsvp() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [status, setStatus] = useState(null);

  const load = () => api.get(`/rsvp/${token}`).then(r=>{setData(r.data); setStatus(r.data.current?.status || null);}).catch(e=>setErr(formatError(e)));

  useEffect(() => {
    (async () => {
      await load();
      const r = params.get("r");
      if (r && ["yes","no","maybe"].includes(r)) {
        try { await api.post(`/rsvp/${token}`, { status: r }); setStatus(r); toast.success(`RSVP saved: ${LABELS[r]}`); }
        catch (e) { toast.error(formatError(e)); }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const respond = async (s) => {
    try { await api.post(`/rsvp/${token}`, { status: s }); setStatus(s); toast.success(`RSVP saved: ${LABELS[s]}`); }
    catch (e) { toast.error(formatError(e)); }
  };

  if (err) return <div className="min-h-screen grid place-items-center p-4"><Card className="p-8 max-w-md text-center"><h1 className="font-serif text-2xl mb-2">Link not valid</h1><p className="text-sm text-muted-foreground">{err}</p></Card></div>;
  if (!data) return <div className="p-10 text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-xl mx-auto py-10 space-y-6">
        <div className="flex flex-col items-center text-center">
          <img
            src="/images/brand-mark.png"
            alt="The Common Dinner"
            data-testid="rsvp-brand-logo"
            className="w-32 h-32 object-contain"
          />
          <div className="font-serif text-2xl leading-tight mt-2">The Common Dinner</div>
          <div className="text-xs text-muted-foreground">Find a dinner that works for everyone.</div>
        </div>
        <Card className="p-6">
          <div className="overline mb-1">RSVP for {data.member?.first_name}</div>
          <h1 className="font-serif text-3xl">{data.restaurant?.name || "Dinner"}</h1>
          <div className="text-sm mono text-muted-foreground mt-1">{data.dinner.date} · {formatTime(data.dinner.time)} · {data.dinner.party_size} guests</div>
          {data.restaurant && <div className="text-sm mt-3">{data.restaurant.address}, {data.restaurant.city}, {data.restaurant.state}</div>}
          {data.restaurant?.phone && <div className="text-xs mono mt-1">{data.restaurant.phone}</div>}
          {status && (
            <div className="mt-4 inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
              Current: {LABELS[status]}
            </div>
          )}
        </Card>
        <Card className="p-6">
          <h3 className="font-serif text-xl mb-3">Your answer</h3>
          <div className="grid grid-cols-3 gap-2">
            <Button data-testid="rsvp-yes-btn" onClick={()=>respond("yes")} variant={status==="yes"?"default":"outline"} className="h-16 flex flex-col gap-1"><CheckCircle2 className="w-4 h-4"/>I’m in</Button>
            <Button data-testid="rsvp-maybe-btn" onClick={()=>respond("maybe")} variant={status==="maybe"?"default":"outline"} className="h-16 flex flex-col gap-1"><HelpCircle className="w-4 h-4"/>Maybe</Button>
            <Button data-testid="rsvp-no-btn" onClick={()=>respond("no")} variant={status==="no"?"default":"outline"} className="h-16 flex flex-col gap-1"><XCircle className="w-4 h-4"/>Can’t</Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3">You can change your answer anytime with this link.</p>
        </Card>
      </div>
    </div>
  );
}
